import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice, SuccessNotice } from '../MissionShell/MissionShell';
import { downloadBlob, readRemittances, RemittanceReaderResult } from '../../api/automationApi';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload one or more remittance PDFs (single or multi-page). The layout can differ from file to file.',
  'Optionally set an output filename (defaults to remittance_output.xlsx).',
  'Click Generate, then download the payments file and/or the debits & negatives file. Review them before using.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function RemittanceReaderPanel({ mission, onBack }: PanelProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [outputFilename, setOutputFilename] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RemittanceReaderResult | null>(null);

  const handleSubmit = async () => {
    if (files.length === 0) {
      setError('Upload at least one PDF first.');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setError(null);
    setResult(null);
    try {
      setResult(await readRemittances(files, outputFilename));
      setStatus('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reading failed.');
      setStatus('error');
    }
  };

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS}>
      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <p className="mb-2 text-sm font-semibold text-foreground">📝 What you get</p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>
            Each page is read line by line; the table heading (Date, Ref, Supplier Invoice, Invoice
            Number, Amount, Gross Amount, Charge …) is found and every row under it is picked up.
          </li>
          <li>One sheet, "Extracted": file, date, invoice no / reference, type, description and amount.</li>
          <li>If a table has no reference column, the reference is taken from the description.</li>
          <li>Scanned (image-only) PDFs are read with OCR only when Tesseract is installed on the server.</li>
        </ul>
      </div>

      <FileDrop
        label="Remittance PDFs"
        required
        multiple
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={files}
        onFiles={setFiles}
      />

      <div className="mb-6 rounded-lg border border-border bg-muted p-4">
        <Label htmlFor="rr-output" className="mb-1.5">
          Output filename (optional)
        </Label>
        <Input
          id="rr-output"
          value={outputFilename}
          onChange={(e) => setOutputFilename(e.target.value)}
          placeholder="remittance_output.xlsx"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Extracting…' : 'Generate'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Extracting…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <SuccessNotice>
          <p>✅ Generated.</p>
          <p className="mt-1 text-xs text-green-800">
            {result.summary.rows} row(s) read from {result.summary.files_with_rows} of {result.summary.files}{' '}
            file(s): {result.summary.positive_rows} payments, {result.summary.negative_rows} debits or negatives.
          </p>
          {result.summary.notes.length > 0 && (
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-amber-800">
              {result.summary.notes.map((note, i) => (
                <li key={i}>{note}</li>
              ))}
            </ul>
          )}
          <div className="mt-3 flex flex-col gap-3 sm:flex-row">
            <Button
              onClick={() => downloadBlob(result.positive.blob, result.positive.filename)}
              style={{ backgroundColor: mission.color, color: '#fff' }}
              className="w-full hover:opacity-90"
            >
              Download Payments File
            </Button>
            <Button
              onClick={() => downloadBlob(result.negative.blob, result.negative.filename)}
              style={{ backgroundColor: mission.color, color: '#fff' }}
              className="w-full hover:opacity-90"
            >
              Download Debits or Negatives File
            </Button>
          </div>
        </SuccessNotice>
      )}
      {status === 'success' && result && (
        <InfoNotice>
          <p className="font-semibold">Next steps</p>
          <ol className="mt-1 list-decimal space-y-1 pl-5">
            <li>Review and verify the downloaded Excel file.</li>
            <li>Open Clickinko and log in.</li>
            <li>
              Press the keys below:
              <ul className="mt-1 list-disc space-y-1 pl-5">
                <li>
                  <strong>Start:</strong> <code className="text-xs">Ctrl + Alt + B</code>
                </li>
                <li>
                  <strong>Pause / Resume:</strong> <code className="text-xs">F9</code>
                </li>
                <li>
                  <strong>Stop:</strong> <code className="text-xs">F12</code>
                </li>
              </ul>
            </li>
          </ol>
        </InfoNotice>
      )}
      {status === 'idle' && <InfoNotice>📁 Upload one or more PDFs above to get started.</InfoNotice>}
    </MissionShell>
  );
}

export default RemittanceReaderPanel;
